import {stableIdentity} from './identity';

type LayoutScope = Element | Document | string | Iterable<Element>;

const MOTION_KEY_ATTR = 'data-ui-motion-key';
const MOTION_LAYOUT_ATTR = 'data-ui-motion-layout';

const isIterable = (value: unknown): value is Iterable<Element> => (
    typeof value === 'object'
    && value !== null
    && Symbol.iterator in value
);

const scopeRoots = (scope: LayoutScope = document): Array<Element | Document> => {
    if (typeof scope === 'string') return Array.from(document.querySelectorAll(scope));
    if (scope instanceof Document || scope instanceof Element) return [scope];
    if (isIterable(scope)) return Array.from(scope);
    return [document];
};

const markLayoutParticipant = (
    scope: LayoutScope = document,
    options: {key?: string; layout?: boolean} = {},
): (() => void) => {
    const elements = scopeRoots(scope).filter((root): root is Element => root instanceof Element);
    const changed: Array<{element: Element; attr: string; previous: string | null}> = [];
    const setTemporaryAttr = (element: Element, attr: string, value: string): void => {
        changed.push({element, attr, previous: element.getAttribute(attr)});
        element.setAttribute(attr, value);
    };

    elements.forEach((element) => {
        if (options.layout ?? true) setTemporaryAttr(element, MOTION_LAYOUT_ATTR, '');
        const identity = stableIdentity(element, options.key);
        if (identity && !element.hasAttribute(MOTION_KEY_ATTR)) {
            setTemporaryAttr(element, MOTION_KEY_ATTR, identity);
        }
    });

    return () => {
        changed.reverse().forEach(({element, attr, previous}) => {
            if (previous === null) {
                element.removeAttribute(attr);
                return;
            }
            element.setAttribute(attr, previous);
        });
    };
};

export {MOTION_KEY_ATTR, MOTION_LAYOUT_ATTR, markLayoutParticipant};
export type {LayoutScope};
